// =============================================================================
// src/features/analytics/analytics-feed-pagination-markup.ts — Nav do feed.
//
// 2026-09-20. Direcao escolhida pelo usuario nos comps: SALTO DIRETO (campo de
// pagina + setas), nao a numeracao classica. O motivo e a escala real medida no
// banco — o recorte padrao tem 21.195 mencoes, 848 paginas. Numerar isso vira
// "1 2 3 … 848": os numeros do meio somem em reticencias e clicar de uma em uma
// nao leva a lugar nenhum. O campo aceita o numero e vai.
//
// Puro: sem DOM, sem fetch. Quem liga os eventos e analytics-view-controls.
// =============================================================================

import { escapeHtml } from '../../app/dom';

/** Estado da nav para um render. `totalItems` null = a contagem nao respondeu. */
export interface AnalyticsFeedPageView {
  readonly page: number;
  readonly pageSize: number;
  readonly totalItems: number | null;
  readonly hasNextPage: boolean;
  readonly isLoading: boolean;
}

function analyticsPtBrInteger(value: number): string {
  return Math.round(value).toLocaleString('pt-BR');
}

/**
 * Ultima pagina, ou null quando nao ha contagem para dividir.
 *
 * @example analyticsFeedLastPage({ totalItems: 21195, pageSize: 25, ... }) // 848
 */
export function analyticsFeedLastPage(pageView: AnalyticsFeedPageView): number | null {
  if (pageView.totalItems === null) return null;
  return Math.max(1, Math.ceil(pageView.totalItems / pageView.pageSize));
}

/**
 * Intervalo exibido: "51–75 de 21.195 mencoes". Sem contagem, so a pagina — o
 * rotulo nunca inventa um total.
 *
 * @example analyticsFeedRangeLabel({ page: 3, pageSize: 25, totalItems: 21195, ... }) // '51–75 de 21.195 menções'
 */
export function analyticsFeedRangeLabel(pageView: AnalyticsFeedPageView): string {
  if (pageView.totalItems === null) return `Página ${analyticsPtBrInteger(pageView.page)}`;
  if (pageView.totalItems === 0) return 'Nenhuma menção neste recorte';
  const first = (pageView.page - 1) * pageView.pageSize + 1;
  const last = Math.min(pageView.page * pageView.pageSize, pageView.totalItems);
  return `${analyticsPtBrInteger(first)}–${analyticsPtBrInteger(last)} de ${analyticsPtBrInteger(pageView.totalItems)} menções`;
}

function analyticsFeedArrowMarkup(direction: 'prev' | 'next', disabled: boolean): string {
  const path = direction === 'prev' ? 'M6.5 2L3.5 5L6.5 8' : 'M3.5 2L6.5 5L3.5 8';
  const label = direction === 'prev' ? 'Página anterior' : 'Próxima página';
  return `<button type="button" class="u-analytics-feed-page-arrow" data-analytics-feed-page="${direction}"${disabled ? ' disabled' : ''} aria-label="${label}">`
    + `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="${path}" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path></svg>`
    + '</button>';
}

function analyticsFeedTotalSuffix(lastPage: number | null): string {
  return lastPage === null ? '' : `<span class="u-analytics-feed-page-total">de ${analyticsPtBrInteger(lastPage)}</span>`;
}

/**
 * Nav da paginacao. String vazia quando nao ha o que paginar — recorte vazio ou
 * conjunto que cabe numa pagina so; o estado vazio da lista assume dali.
 *
 * O paragrafo do intervalo e' `aria-live="polite"`: e ELE que anuncia a troca de
 * pagina para leitor de tela (a lista de cards nao e live region).
 *
 * @example buildAnalyticsFeedPaginationMarkup({ page: 3, pageSize: 25, totalItems: 21195, hasNextPage: true, isLoading: false })
 */
export function buildAnalyticsFeedPaginationMarkup(pageView: AnalyticsFeedPageView): string {
  const lastPage = analyticsFeedLastPage(pageView);
  if (pageView.totalItems === 0) return '';
  if (lastPage === 1 && !pageView.hasNextPage) return '';
  return `<nav class="u-analytics-feed-pagination" id="analyticsFeedPagination" aria-label="Paginação das menções" aria-busy="${pageView.isLoading ? 'true' : 'false'}">
    <p class="u-analytics-feed-range" aria-live="polite">${escapeHtml(analyticsFeedRangeLabel(pageView))}</p>
    <div class="u-analytics-feed-page-jump">
      ${analyticsFeedArrowMarkup('prev', pageView.isLoading || pageView.page <= 1)}
      <label for="analyticsFeedPageInput">Página</label>
      <input id="analyticsFeedPageInput" type="text" inputmode="numeric" autocomplete="off" data-analytics-feed-page-input value="${pageView.page}"${pageView.isLoading ? ' disabled' : ''}>
      ${analyticsFeedTotalSuffix(lastPage)}
      ${analyticsFeedArrowMarkup('next', pageView.isLoading || !pageView.hasNextPage)}
    </div>
  </nav>`;
}
